import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Gavel,
  FileText,
  Settings,
  Monitor,
  Vote,
  LogOut,
  ArrowLeftRight,
  Home,
  Menu,
  X,
  BookOpen,
  CircleHelp,
  Mic2,
  CircleStop,
} from 'lucide-react';
import Timer from '../common/Timer';
import QuorumStatus from '../common/QuorumStatus';
import { getQuorumStatus } from '../../utils/quorumUtils';
import { useAuth } from '../../auth/AuthContext';
import { hasSupabaseConfig } from '../../lib/supabaseClient';
import SignedInUserLine from '../common/SignedInUserLine';

const MeetingHeader = ({
  globalTheme,
  meetingSetup,
  meetingTimer,
  isTimerRunning,
  onToggleTimer,
  onExportMinutes,
  onOpenSettings,
  onOpenTutorial = null,
  onOpenQuorumAdjust,
  currentView,
  onViewChange,
  orgId = null,
  canMeeting = true,
  canCommittees = true,
  canElections = true,
  voteJoinCode = null,
  voteRoomError = null,
  voteRoomBusy = false,
  onEnsureVoteRoom = null,
  onLaunchChairPrompt = null,
  driverSeat = 'local',
  onReturnControl = null,
  onReturnToChairPrompt = null,
  officerSessionId = null,
  officerPromptUrl = null,
  officerObserverUrl = null,
  onEndLiveMeeting = null,
}) => {
  const { session, signOut, memberships } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const assisted = meetingSetup?.sessionMode === 'assisted';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const launchObserverWindow = async () => {
    if (onEnsureVoteRoom) {
      await onEnsureVoteRoom();
    }
    const observerUrl =
      officerObserverUrl ||
      (() => {
        const params = new URLSearchParams(window.location.search);
        params.set('observer', '1');
        params.set('seat', 'observer');
        params.delete('chairPrompt');
        const qs = params.toString();
        return `${window.location.origin}${window.location.pathname}?${qs}#observer`;
      })();
    const observerWindow = window.open(
      observerUrl,
      'ObserverView',
      'width=1920,height=1080,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
    );

    if (observerWindow) {
      observerWindow.focus();
      observerWindow.addEventListener('load', () => {
        observerWindow.postMessage({ type: 'SET_OBSERVER_MODE' }, window.location.origin);
        setTimeout(() => {
          observerWindow.postMessage({ type: 'REQUEST_CURRENT_STATE' }, window.location.origin);
        }, 500);
      });
      if (window.observerWindows) {
        window.observerWindows.push(observerWindow);
      } else {
        window.observerWindows = [observerWindow];
      }
    } else {
      alert('Allow pop-ups for this site to open the observer window.');
    }
    setMenuOpen(false);
  };

  const copyLink = async (url, label) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      alert(`${label} link copied. Open it on another signed-in device.`);
    } catch {
      window.prompt('Copy this link:', url);
    }
    setMenuOpen(false);
  };

  const launchChairPromptWindow = () => {
    if (typeof onLaunchChairPrompt === 'function') {
      onLaunchChairPrompt();
      setMenuOpen(false);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('chairPrompt', '1');
    params.delete('observer');
    const qs = params.toString();
    const promptUrl = `${window.location.origin}${window.location.pathname}?${qs}#chair-prompt`;
    const promptWindow = window.open(
      promptUrl,
      'ChairPromptView',
      'width=900,height=1100,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
    );
    if (promptWindow) {
      promptWindow.focus();
      promptWindow.addEventListener('load', () => {
        promptWindow.postMessage({ type: 'SET_CHAIR_PROMPT_MODE' }, window.location.origin);
        setTimeout(() => {
          promptWindow.postMessage({ type: 'REQUEST_CURRENT_STATE' }, window.location.origin);
        }, 500);
      });
      if (window.chairPromptWindows) {
        window.chairPromptWindows.push(promptWindow);
      } else {
        window.chairPromptWindows = [promptWindow];
      }
    } else {
      alert('Allow pop-ups for this site to open the Chair Dashboard.');
    }
    setMenuOpen(false);
  };

  const navigationItems = [
    { id: 'meeting', label: 'Meeting', icon: Gavel, allowed: canMeeting },
    { id: 'committees', label: 'Committees', icon: FileText, allowed: canCommittees },
    { id: 'elections', label: 'Elections', icon: Vote, allowed: canElections },
  ].filter((item) => item.allowed);

  const orgHome = orgId ? `/orgs/${orgId}` : '/orgs';
  const showAuthLinks = hasSupabaseConfig() && session;

  const menuItemClass =
    'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-pp-ink hover:bg-pp-paper';

  return (
    <header className="sticky top-0 z-40 border-b border-pp-line/80 bg-pp-surface/95 backdrop-blur-md shadow-pp">
      <div className="mx-auto flex h-14 max-w-[1600px] items-center gap-3 px-3 sm:gap-4 sm:px-5">
        {/* Brand + signed-in role */}
        <div className="flex min-w-0 shrink items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pp-accent text-white">
            <Gavel className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="hidden truncate text-sm font-semibold leading-tight text-pp-ink sm:block">
              Pocket Parliamentarian
            </p>
            {meetingSetup.organizationName ? (
              <p className="hidden truncate text-[11px] font-medium leading-tight text-pp-muted sm:block">
                {meetingSetup.organizationName}
              </p>
            ) : null}
            <div className="flex min-w-0 items-center gap-1.5">
              <SignedInUserLine className="min-w-0" />
              {assisted && (
                <span
                  data-tour="driver-badge"
                  className={`inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${
                    driverSeat === 'remote'
                      ? 'bg-amber-50 text-amber-950 ring-amber-200'
                      : 'bg-pp-sageSoft text-pp-sage ring-pp-sage/30'
                  }`}
                  title={
                    driverSeat === 'remote'
                      ? 'Chair requested control — Motions still run on this Driver window'
                      : 'Secretary-assisted: you are driving this console'
                  }
                >
                  {driverSeat === 'remote' ? 'Chair has control' : 'Driver'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Primary module nav */}
        <nav className="flex min-w-0 flex-1 items-center justify-center" aria-label="Primary">
          <div className="inline-flex max-w-full items-center gap-0.5 overflow-x-auto rounded-full bg-pp-paper p-0.5 ring-1 ring-pp-line/60">
            {navigationItems.map((item) => {
              const active = currentView === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-tour={`nav-${item.id}`}
                  onClick={() => onViewChange(item.id)}
                  className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition-all sm:px-3 sm:text-sm ${
                    active
                      ? 'bg-pp-accent text-white shadow-sm shadow-pp-accent/25'
                      : 'text-pp-muted hover:bg-white hover:text-pp-ink'
                  }`}
                >
                  <item.icon
                    className={`h-3.5 w-3.5 shrink-0 ${active ? 'text-white' : 'text-pp-muted'}`}
                  />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* Live status + observer + menu */}
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {hasSupabaseConfig() && meetingSetup.enableQrDeviceVoting && voteJoinCode ? (
            <div
              className="hidden lg:flex flex-col items-end rounded-lg border border-pp-line bg-pp-paper px-2 py-1 text-right"
              title="Voting room code — also shown on Observer"
            >
              <span className="text-[10px] font-medium uppercase tracking-wide text-pp-muted">Vote code</span>
              <span className="font-mono text-sm font-bold tracking-widest text-pp-accent">{voteJoinCode}</span>
            </div>
          ) : null}
          {assisted && driverSeat === 'remote' && typeof onReturnControl === 'function' && (
            <button
              type="button"
              data-tour="return-control"
              onClick={() => onReturnControl()}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-pp-sage/50 bg-white px-2.5 text-xs font-semibold text-pp-sage transition-colors hover:bg-pp-sageSoft sm:px-3 sm:text-sm"
              title="Hand Motions back to the secretary"
              aria-label="Return control to secretary"
            >
              Return control
            </button>
          )}
          <button
            type="button"
            data-tour="launch-observer"
            onClick={() => void launchObserverWindow()}
            disabled={voteRoomBusy}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-pp-accent px-2.5 text-xs font-semibold text-white shadow-sm shadow-pp-accent/25 transition-colors hover:bg-pp-accent/90 disabled:opacity-60 sm:px-3 sm:text-sm"
            title={
              !meetingSetup.enableQrDeviceVoting
                ? 'Open observer window'
                : voteRoomError
                  ? voteRoomError
                  : voteJoinCode
                    ? `Open observer (vote code ${voteJoinCode})`
                    : 'Open observer window'
            }
            aria-label="Launch observer"
          >
            <Monitor className="h-4 w-4 shrink-0" aria-hidden />
            <span>{voteRoomBusy ? 'Preparing…' : 'Launch observer'}</span>
          </button>

          <div className="shrink-0 rounded-lg border border-pp-line/80 bg-pp-paper/50 px-1.5 py-0.5" data-tour="meeting-timer">
            <Timer
              time={meetingTimer}
              isRunning={isTimerRunning}
              onToggle={onToggleTimer}
              theme={globalTheme}
              size="normal"
            />
          </div>

          {meetingSetup.totalMembers > 0 && (
            <div className="hidden shrink-0 md:block">
              <QuorumStatus
                quorumStatus={getQuorumStatus(meetingSetup)}
                onClick={onOpenQuorumAdjust}
                size="normal"
                className="shrink-0 shadow-sm"
              />
            </div>
          )}

          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                menuOpen
                  ? 'border-pp-accent bg-pp-accent/10 text-pp-accent'
                  : 'border-pp-line bg-pp-surface text-pp-muted hover:bg-pp-paper hover:text-pp-ink'
              }`}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              title="Menu"
            >
              {menuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>

            {menuOpen && (
              <div
                role="menu"
                className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-pp-line bg-pp-surface py-1 shadow-pp-lg"
              >
                {showAuthLinks && (
                  <>
                    <Link
                      to={orgHome}
                      role="menuitem"
                      className={menuItemClass}
                      onClick={() => setMenuOpen(false)}
                    >
                      <Home className="h-4 w-4 text-pp-muted" />
                      Organization home
                    </Link>
                    <div className="my-1 border-t border-pp-line" />
                  </>
                )}

                {meetingSetup.totalMembers > 0 && (
                  <button
                    type="button"
                    role="menuitem"
                    className={`${menuItemClass} md:hidden`}
                    onClick={() => {
                      onOpenQuorumAdjust();
                      setMenuOpen(false);
                    }}
                  >
                    Quorum status
                  </button>
                )}

                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  data-tour="menu-observer-tab"
                  onClick={() => {
                    onViewChange('observer');
                    setMenuOpen(false);
                  }}
                >
                  <Monitor className="h-4 w-4 text-pp-muted" />
                  Observer view (this tab)
                </button>
                {assisted && (
                  <button
                    type="button"
                    role="menuitem"
                    className={menuItemClass}
                    data-tour="launch-chair-prompt"
                    onClick={launchChairPromptWindow}
                  >
                    <Mic2 className="h-4 w-4 text-pp-muted" />
                    Open Chair Dashboard
                  </button>
                )}
                {assisted && (
                  <button
                    type="button"
                    role="menuitem"
                    className={menuItemClass}
                    data-tour="menu-chair-prompt-tab"
                    onClick={() => {
                      onViewChange('chair-prompt');
                      setMenuOpen(false);
                    }}
                  >
                    <Mic2 className="h-4 w-4 text-pp-muted" />
                    Chair Dashboard (this tab)
                  </button>
                )}
                {officerSessionId && officerPromptUrl && (
                  <button
                    type="button"
                    role="menuitem"
                    className={menuItemClass}
                    data-tour="menu-copy-prompt-link"
                    onClick={() => void copyLink(officerPromptUrl, 'Chair Dashboard')}
                  >
                    <Mic2 className="h-4 w-4 text-pp-muted" />
                    Copy Chair Dashboard link
                  </button>
                )}
                {officerSessionId && officerObserverUrl && (
                  <button
                    type="button"
                    role="menuitem"
                    className={menuItemClass}
                    data-tour="menu-copy-observer-link"
                    onClick={() => void copyLink(officerObserverUrl, 'Observer')}
                  >
                    <Monitor className="h-4 w-4 text-pp-muted" />
                    Copy Observer link
                  </button>
                )}
                {assisted && driverSeat === 'remote' && typeof onReturnControl === 'function' && (
                  <button
                    type="button"
                    role="menuitem"
                    className={menuItemClass}
                    data-tour="menu-return-control"
                    onClick={() => {
                      onReturnControl();
                      setMenuOpen(false);
                    }}
                  >
                    <ArrowLeftRight className="h-4 w-4 text-pp-muted" />
                    Return control to secretary
                  </button>
                )}
                {assisted &&
                  driverSeat === 'remote' &&
                  currentView === 'meeting' &&
                  typeof onReturnToChairPrompt === 'function' && (
                    <button
                      type="button"
                      role="menuitem"
                      className={menuItemClass}
                      data-tour="menu-return-prompt"
                      onClick={() => {
                        onReturnToChairPrompt();
                        setMenuOpen(false);
                      }}
                    >
                      <Mic2 className="h-4 w-4 text-pp-muted" />
                      Return to Chair Dashboard
                    </button>
                  )}

                <div className="my-1 border-t border-pp-line" />

                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  data-tour="menu-meeting-assistant"
                  onClick={() => {
                    if (currentView !== 'meeting') {
                      onViewChange('meeting');
                    }
                    setMenuOpen(false);
                    window.setTimeout(() => {
                      window.dispatchEvent(new CustomEvent('pp-focus-meeting-assistant'));
                    }, 50);
                  }}
                >
                  <BookOpen className="h-4 w-4 text-pp-muted" />
                  Ask the assistant
                </button>

                <div className="my-1 border-t border-pp-line" />

                <p className="px-3 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wide text-pp-muted">
                  Export minutes
                </p>
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  data-tour="export-minutes"
                  onClick={() => {
                    onExportMinutes('pdf');
                    setMenuOpen(false);
                  }}
                >
                  <FileText className="h-4 w-4 text-pp-muted" />
                  PDF
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  data-tour="export-minutes-word"
                  onClick={() => {
                    onExportMinutes('doc');
                    setMenuOpen(false);
                  }}
                >
                  <FileText className="h-4 w-4 text-pp-muted" />
                  Word
                </button>

                <div className="my-1 border-t border-pp-line" />

                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  onClick={() => {
                    onOpenSettings();
                    setMenuOpen(false);
                  }}
                >
                  <Settings className="h-4 w-4 text-pp-muted" />
                  Settings
                </button>
                {officerSessionId && typeof onEndLiveMeeting === 'function' ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={`${menuItemClass} text-rose-800 hover:bg-rose-50`}
                    data-tour="menu-end-live-meeting"
                    onClick={() => {
                      onEndLiveMeeting();
                      setMenuOpen(false);
                    }}
                  >
                    <CircleStop className="h-4 w-4 text-rose-700" />
                    End live meeting
                  </button>
                ) : null}
                {onOpenTutorial ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={menuItemClass}
                    onClick={() => {
                      onOpenTutorial();
                      setMenuOpen(false);
                    }}
                  >
                    <CircleHelp className="h-4 w-4 text-pp-muted" />
                    Tutorial
                  </button>
                ) : null}

                {showAuthLinks && (
                  <>
                    <div className="my-1 border-t border-pp-line" />
                    {memberships.length > 1 && (
                      <Link
                        to="/orgs"
                        role="menuitem"
                        className={menuItemClass}
                        onClick={() => setMenuOpen(false)}
                      >
                        <ArrowLeftRight className="h-4 w-4 text-pp-muted" />
                        Switch organization
                      </Link>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      className={menuItemClass}
                      onClick={() => {
                        setMenuOpen(false);
                        void signOut();
                      }}
                    >
                      <LogOut className="h-4 w-4 text-pp-muted" />
                      Sign out
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default MeetingHeader;
